import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Banner, BlockStack, Button, ButtonGroup, Card, InlineGrid, InlineStack, Select, Text } from '@shopify/polaris';
import { getRequest, simplePostRequest } from '../../../../shared/components/functions';
import { formatInteger, formatPercent, toNumber } from '../deep/analyticsHelpers';
import { eur } from '../deep/cashPlanningShared';
import { MODES, RANGES, addYears, bucketSeries, buildBuckets, dailyMap, localToday, orderbookAt, orderbookSeries, prepareOrders, recentMonthBuckets, seriesFromDate } from './dashboardData.mjs';
import { COLORS, formatEur, ratioFormatter } from './chartTheme';
import KpiTile from './KpiTile';
import PeriodChartCard from './PeriodChartCard';
import OpenPositionsCard from './OpenPositionsCard';
import WatchlistCard from './WatchlistCard';
import CashPlanningCard, { DASHBOARD_HORIZON, planOutlook } from './CashPlanningCard';

/*
  Executive dashboard: the state of the business on one page.

    tiles    year-to-date and run-rate projections (revenue, gross margin, order intake), orderbook,
             bank balance and overdue receivables — from /api/dashboard/summary and the cash plan
    charts   order intake, revenue, costs, gross margin per period and the orderbook value at the end
             of each period, always against the comparable period a year earlier. Daily rows come from
             /api/dashboard/series and /api/dashboard/orderbook once per range; the grouping toggle
             re-buckets them client-side (dashboardData.mjs).
    watch    the watchlist in the narrow column next to the cash card (server-side, /api/dashboard/watchlist; the
             cash items need the plan, so it is requested after the plan has loaded and the cache is warm)
    cash     the open positions table and the cash planning balance chart (shared with that tab)
    digest   "Email digest now" sends the Monday morning email (cron/dashboardDigest.js) on demand

  Grouping and range live in the url (?g=WEEK&r=5m) so a reload or a shared link keeps them.
*/

const MODE_KEYS = MODES.map((mode) => mode.key);
const RANGE_KEYS = RANGES.map((range) => range.key);

function useDashboardParams() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const mode = MODE_KEYS.includes(params.get('g')) ? params.get('g') : 'WEEK';
  const rangeKey = RANGE_KEYS.includes(params.get('r')) ? params.get('r') : '5m';

  const setParam = useCallback(
    (key, value) => {
      const next = new URLSearchParams(location.search);
      next.set(key, value);
      navigate(`${location.pathname}?${next.toString()}`, { replace: true });
    },
    [navigate, location.search, location.pathname]
  );

  return { mode, rangeKey, setMode: (value) => setParam('g', value), setRangeKey: (value) => setParam('r', value) };
}

export default function DashboardTab({ links = {} }) {
  const today = useMemo(() => localToday(), []);
  const { mode, rangeKey, setMode, setRangeKey } = useDashboardParams();
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [series, setSeries] = useState(null);
  const [orderbook, setOrderbook] = useState(null);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planError, setPlanError] = useState(null);
  const [watchlist, setWatchlist] = useState(null);
  const [watchlistError, setWatchlistError] = useState(null);
  const [digestStatus, setDigestStatus] = useState(null); // { sending, message, tone }
  const loadedFrom = useRef(null);

  const range = RANGES.find((item) => item.key === rangeKey) || RANGES[0];
  const rangeStart = range.start(today);
  const from = seriesFromDate(rangeStart);

  useEffect(() => {
    getRequest('/api/dashboard/summary').then((result) => {
      if (!result || result.error) setSummaryError(result?.error || 'Could not load the summary');
      else setSummary(result);
    });
  }, []);

  const loadPlan = useCallback(async () => {
    setPlanError(null);
    const result = await getRequest(`/api/cash-planning/plan?horizon=${DASHBOARD_HORIZON}`);
    if (!result || result.error) setPlanError(result?.error || 'Could not load the cash plan');
    else setPlan(result);
  }, []);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  // the watchlist waits for the plan (or its failure) so the cash items come from the same, already computed plan
  useEffect(() => {
    if (!plan && !planError) return undefined;
    let cancelled = false;
    getRequest(`/api/dashboard/watchlist${plan ? '?cash=1' : ''}`).then((result) => {
      if (cancelled) return;
      if (!result || result.error) setWatchlistError(result?.error || 'Could not load the watchlist');
      else setWatchlist(result);
    });
    return () => {
      cancelled = true;
    };
  }, [plan, planError]);

  const sendDigest = async () => {
    setDigestStatus({ sending: true, message: 'Sending…', tone: 'subdued' });
    try {
      const response = await simplePostRequest('/api/cron/trigger-dashboard-digest?force=true', {});
      const body = await response.json().catch(() => ({}));
      if (response.ok && body.sent) setDigestStatus({ sending: false, message: `Sent to ${(body.deliveredTo || body.recipients || []).join(', ')}`, tone: 'success' });
      else setDigestStatus({ sending: false, message: body.error || 'The digest could not be sent', tone: 'critical' });
    } catch (err) {
      setDigestStatus({ sending: false, message: 'The digest could not be sent', tone: 'critical' });
    }
  };

  // daily rows are fetched once per window; a wider range refetches, a narrower one or another grouping reuses them
  useEffect(() => {
    if (loadedFrom.current && loadedFrom.current <= from) return undefined;
    let cancelled = false;
    setLoadingSeries(true);
    Promise.all([getRequest(`/api/dashboard/series?from=${from}`), getRequest(`/api/dashboard/orderbook?from=${from}`)]).then(([seriesResult, orderbookResult]) => {
      if (cancelled) return;
      if (seriesResult && !seriesResult.error) {
        setSeries({
          from,
          intake: dailyMap(seriesResult.intake),
          revenue: dailyMap(seriesResult.invoicesOut, seriesResult.creditNotesOut),
          costs: dailyMap(seriesResult.invoicesIn, seriesResult.creditNotesIn)
        });
      }
      if (orderbookResult && !orderbookResult.error) setOrderbook({ from, orders: prepareOrders(orderbookResult.orders, orderbookResult.invoices) });
      loadedFrom.current = from;
      setLoadingSeries(false);
    });
    return () => {
      cancelled = true;
    };
  }, [from]);

  const buckets = useMemo(() => buildBuckets({ rangeStart, today, mode }), [rangeStart, today, mode]);

  const charts = useMemo(() => {
    if (!series) return null;
    const revenue = bucketSeries(buckets, series.revenue);
    const costs = bucketSeries(buckets, series.costs);
    const margin = revenue.map((point, index) => ({
      ...point,
      current: { v: point.current.v - costs[index].current.v, n: 0 },
      previous: { v: point.previous.v - costs[index].previous.v, n: 0 }
    }));
    return { intake: bucketSeries(buckets, series.intake), revenue, costs, margin };
  }, [series, buckets]);

  const orderbookPoints = useMemo(() => (orderbook ? orderbookSeries(buckets, orderbook.orders, today) : null), [orderbook, buckets, today]);

  // twelve monthly points for the tile sparklines, and the orderbook a year ago for its delta
  const sparks = useMemo(() => {
    if (!series) return {};
    const months = recentMonthBuckets(today, 12);
    const monthly = (byDay) => bucketSeries(months, byDay).map((point) => point.current.v);
    const revenue = monthly(series.revenue);
    const costs = monthly(series.costs);
    return {
      intake: monthly(series.intake),
      revenue,
      margin: revenue.map((value, index) => value - costs[index]),
      orderbook: orderbook ? orderbookSeries(months, orderbook.orders, today).map((point) => point.current.v) : null,
      orderbookYearAgo: orderbook ? orderbookAt(orderbook.orders, addYears(today, -1)).v : null
    };
  }, [series, orderbook, today]);

  const outlook = useMemo(() => planOutlook(plan), [plan]);

  // tile figures
  const year = Number(today.substring(0, 4));
  const previousYear = year - 1;
  const factor = toNumber(summary?.projection?.factor) || 1;
  const revenueYtd = toNumber(summary?.revenue?.ytd);
  const costsYtd = toNumber(summary?.costs?.ytd);
  const marginYtd = revenueYtd - costsYtd;
  const marginSamePeriodLastYear = toNumber(summary?.revenue?.samePeriodLastYear) - toNumber(summary?.costs?.samePeriodLastYear);
  const marginLastYear = toNumber(summary?.revenue?.lastYear) - toNumber(summary?.costs?.lastYear);
  const marginPct = revenueYtd ? marginYtd / revenueYtd : null;
  const marginPctLastYear = toNumber(summary?.revenue?.lastYear) ? marginLastYear / toNumber(summary.revenue.lastYear) : null;
  const intakeYtd = toNumber(summary?.intake?.ytd);
  const bookToBill = revenueYtd ? intakeYtd / revenueYtd : null;
  const receivables = summary?.openPositions?.find((row) => row.key === 'invoices_out');
  const receivablesOpen = toNumber(receivables?.open);
  const receivablesOverdue = toNumber(receivables?.overdue);
  const dso = summary?.trailing365?.revenueIncl ? (receivablesOpen / toNumber(summary.trailing365.revenueIncl)) * 365 : null;
  const samePeriodLabel = `vs same period ${previousYear}`;
  const fullYearLabel = `vs ${previousYear} full year`;

  return (
    <BlockStack gap="400">
      {summaryError ? (
        <Banner tone="critical" title="The dashboard summary could not be loaded">
          <p>{summaryError}</p>
        </Banner>
      ) : null}

      <Card padding="300">
        <InlineStack align="space-between" blockAlign="center" gap="400" wrap>
          <InlineStack gap="400" blockAlign="center" wrap>
            <Text variant="headingSm" as="h3">
              Group by
            </Text>
            <ButtonGroup variant="segmented">
              {MODES.map((item) => (
                <Button key={item.key} size="slim" pressed={mode === item.key} onClick={() => setMode(item.key)}>
                  {item.label}
                </Button>
              ))}
            </ButtonGroup>
            <div style={{ width: 180 }}>
              <Select label="Range" labelHidden options={RANGES.map((item) => ({ label: item.label, value: item.key }))} value={rangeKey} onChange={setRangeKey} />
            </div>
          </InlineStack>
          <InlineStack gap="300" blockAlign="center" wrap>
            {digestStatus ? (
              <Text as="span" variant="bodySm" tone={digestStatus.tone}>
                {digestStatus.message}
              </Text>
            ) : null}
            <Button variant="plain" onClick={() => window.open('/api/dashboard/digest-preview', '_blank', 'noopener')}>
              Preview digest
            </Button>
            <Button size="slim" onClick={sendDigest} loading={Boolean(digestStatus?.sending)}>
              Email digest now
            </Button>
          </InlineStack>
        </InlineStack>
        <div style={{ marginTop: 8 }}>
          <Text as="p" variant="bodySm" tone="subdued">
            {`Figures as of ${today}. Revenue, costs and order intake excl. VAT, net of credit notes; every period is compared with the same period a year earlier. The digest goes out every Monday at 07:00.`}
          </Text>
        </div>
      </Card>

      <InlineGrid columns={{ xs: 1, sm: 2, lg: 4 }} gap="400">
        <KpiTile
          label={`Revenue ${year} year to date`}
          loading={!summary}
          value={formatEur(revenueYtd)}
          delta={{ current: revenueYtd, previous: summary?.revenue?.samePeriodLastYear, label: samePeriodLabel }}
          detail={summary ? `${formatInteger(summary.customers?.ytd)} customers invoiced · ${formatInteger(summary.customers?.newYtd)} new` : null}
          spark={sparks.revenue}
          accent={COLORS.revenue}
        />
        <KpiTile
          label={`Projected revenue ${year}`}
          loading={!summary}
          value={formatEur(revenueYtd * factor)}
          delta={{ current: revenueYtd * factor, previous: summary?.revenue?.lastYear, label: fullYearLabel }}
          detail={summary ? `run rate after ${formatInteger(summary.projection?.elapsedDays)} days, as on the gross margin graph · ${previousYear}: ${formatEur(summary.revenue?.lastYear)}` : null}
          accent={COLORS.revenue}
        />
        <KpiTile
          label={`Gross margin ${year} year to date`}
          loading={!summary}
          value={formatEur(marginYtd)}
          delta={{ current: marginYtd, previous: marginSamePeriodLastYear, label: samePeriodLabel }}
          detail={summary ? `${marginPct === null ? '—' : formatPercent(marginPct)} of revenue · costs ${formatEur(costsYtd)}` : null}
          spark={sparks.margin}
          accent={COLORS.margin}
          tone={summary && marginYtd < 0 ? 'critical' : undefined}
        />
        <KpiTile
          label={`Projected gross margin ${year}`}
          loading={!summary}
          value={formatEur(marginYtd * factor)}
          delta={{ current: marginYtd * factor, previous: marginLastYear, label: fullYearLabel }}
          detail={
            summary
              ? `${marginPct === null ? '—' : formatPercent(marginPct)} of revenue · ${previousYear}: ${formatEur(marginLastYear)} (${
                  marginPctLastYear === null ? '—' : formatPercent(marginPctLastYear)
                })`
              : null
          }
          accent={COLORS.margin}
          tone={summary && marginYtd < 0 ? 'critical' : undefined}
        />
        <KpiTile
          label={`Order intake ${year} year to date`}
          loading={!summary}
          value={formatEur(intakeYtd)}
          delta={{ current: intakeYtd, previous: summary?.intake?.samePeriodLastYear, label: samePeriodLabel }}
          detail={summary ? `${formatInteger(summary.intake?.ordersYtd)} orders · book-to-bill ${bookToBill === null ? '—' : ratioFormatter.format(bookToBill)}` : null}
          spark={sparks.intake}
          accent={COLORS.intake}
        />
        <KpiTile
          label="Orderbook value"
          loading={!summary}
          value={formatEur(summary?.backlog?.backlogIncl)}
          delta={
            sparks.orderbookYearAgo !== null && sparks.orderbookYearAgo !== undefined ? { current: summary?.backlog?.backlogIncl, previous: sparks.orderbookYearAgo, label: 'vs a year ago' } : null
          }
          detail={summary ? `${formatInteger(summary.backlog?.openOrders)} open orders · sold, not yet invoiced (incl. VAT)` : null}
          spark={sparks.orderbook}
          accent={COLORS.orderbook}
        />
        <KpiTile
          label="Bank balance today"
          loading={!plan && !planError}
          value={planError ? '—' : eur(plan?.openingBalance)}
          detail={planError ? 'cash plan unavailable' : outlook ? `expected in ${outlook.horizon} days: ${eur(outlook.summary.end)} · cautious ${eur(outlook.summary.endCautious)}` : null}
          accent={COLORS.cash}
          tone={outlook && outlook.summary.end < 0 ? 'critical' : undefined}
        />
        <KpiTile
          label="Overdue receivables"
          loading={!summary}
          value={formatEur(receivablesOverdue)}
          detail={
            summary
              ? `${receivablesOpen ? formatPercent(receivablesOverdue / receivablesOpen) : '—'} of ${formatEur(receivablesOpen)} open · DSO ${dso === null ? '—' : `${Math.round(dso)} days`}`
              : null
          }
          accent={COLORS.overdue}
          tone={summary && receivablesOpen && receivablesOverdue / receivablesOpen > 0.5 ? 'critical' : undefined}
        />
      </InlineGrid>

      <InlineGrid columns={{ xs: 1, lg: 2 }} gap="400">
        <PeriodChartCard
          title="Order intake"
          subtitle="New sales value confirmed per period: order lines excl. VAT of non-canceled sale orders, by order date."
          points={charts?.intake}
          accent={COLORS.intake}
          countLabel="orders"
          loading={loadingSeries}
        />
        <PeriodChartCard
          title="Revenue"
          subtitle="Outgoing invoices excl. VAT, net of credit notes: the corrected series of the gross margin graph, by invoice date."
          points={charts?.revenue}
          accent={COLORS.revenue}
          countLabel="invoices"
          loading={loadingSeries}
        />
        <PeriodChartCard
          title="Costs"
          subtitle="Incoming invoices excl. VAT, net of credit notes, by booked date (as on the Costs tab)."
          points={charts?.costs}
          accent={COLORS.costs}
          invert
          countLabel="invoices"
          loading={loadingSeries}
        />
        <PeriodChartCard
          title="Gross margin"
          subtitle="Revenue minus costs per period. Invoicing and booking timing make single weeks noisy; months and quarters tell the real story."
          points={charts?.margin}
          kind="diverging"
          accent={COLORS.margin}
          loading={loadingSeries}
        />
        <PeriodChartCard
          title="Orderbook value"
          subtitle="Sold but not yet invoiced at the end of each period: open sale orders of the previous 24 months minus their invoices, incl. VAT (the order backlog card on the Revenue tab)."
          points={orderbookPoints}
          kind="area"
          accent={COLORS.orderbook}
          countLabel="open orders"
          loading={loadingSeries}
        />
        <OpenPositionsCard summary={summary} cashflowUrl={links.cashflow} />
      </InlineGrid>

      <InlineGrid columns={{ xs: 1, lg: 'minmax(0, 3fr) minmax(0, 1fr)' }} gap="400">
        <CashPlanningCard plan={plan} error={planError} onRetry={loadPlan} planningUrl={links.cashPlanning} />
        <WatchlistCard watchlist={watchlist} loading={!plan && !planError} error={watchlistError} />
      </InlineGrid>
    </BlockStack>
  );
}
